import { queryOptions } from '@tanstack/react-query'
import { api } from './api'

/**
 * Query options are defined once and shared between route loaders (which prefetch) and
 * components (which read). That's what keeps a route from rendering, then discovering it
 * needs data, then showing a spinner — the request is already in flight before the
 * component mounts, which is the waterfall problem SSR was supposed to solve for us.
 *
 * Note the `if (!res.ok) throw` line in each of these. It isn't ceremony: the RPC client
 * types `res.json()` as a union of the success body and the error body, and that check is
 * what narrows it. Drop it and every caller has to discriminate the union itself.
 */
/**
 * Who the current visitor is, as far as the server is concerned — the SPA's only source of auth
 * state now that there's no client auth SDK. The `/app` route loader awaits this before rendering,
 * which is what makes the gate a decision rather than a flicker.
 *
 * `staleTime: 0` on purpose. Everything else here tolerates 30s of staleness; a session does not.
 * Signing out in another tab, or a refresh token being revoked, should be noticed on the next
 * navigation rather than up to half a minute later.
 */
export const sessionQuery = queryOptions({
  queryKey: ['session'],
  queryFn: async () => {
    const res = await api.api.auth.me.$get()
    if (!res.ok) throw new Error('Failed to load session')
    return res.json()
  },
  staleTime: 0,
  retry: false,
})

export const healthQuery = queryOptions({
  queryKey: ['health'],
  queryFn: async () => {
    const res = await api.api.health.$get()
    if (!res.ok) throw new Error('Health check failed')
    return res.json()
  },
})

export * from './queries-rbac'
export * from './queries-accounts'
export * from './queries-topics'
export * from './queries-reports'
export * from './queries-templates'
export * from './queries-settings'
